import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const SITE_URL = 'https://hoosierlifeinsurance.com';
const CANONICAL = `${SITE_URL}/learning-hub`;

interface PillarGuide {
  slug: string;
  title: string;
  excerpt: string;
  readTime: string;
  icon: string;
  accentColor: string;
  bgColor: string;
}

const PILLAR_GUIDES: PillarGuide[] = [
  {
    slug: 'how-much-life-insurance-indiana',
    title: 'How Much Life Insurance Do I Need?',
    excerpt: 'The DIME method with real Indiana mortgage, income, and college cost data. Read this first.',
    readTime: '10 min',
    icon: 'ri-calculator-line',
    accentColor: 'text-[#1A3A52]',
    bgColor: 'bg-[#1A3A52]/8',
  },
  {
    slug: 'term-life-insurance-indianapolis',
    title: 'Term Life Insurance: Complete 2025 Guide',
    excerpt: 'Everything Indianapolis families need to know — costs, lengths, carriers, and how to apply.',
    readTime: '12 min',
    icon: 'ri-time-line',
    accentColor: 'text-[#D97642]',
    bgColor: 'bg-[#D97642]/8',
  },
  {
    slug: 'mortgage-protection-indianapolis',
    title: 'Mortgage Protection Insurance Indianapolis',
    excerpt: 'Keep your family\'s home safe if something happens to you. Costs, coverage, and how it works.',
    readTime: '9 min',
    icon: 'ri-home-heart-line',
    accentColor: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
  },
  {
    slug: 'no-medical-exam-life-insurance-indiana',
    title: 'No Medical Exam Life Insurance Indiana',
    excerpt: 'Same-day coverage, no blood draws. Simplified issue vs. guaranteed issue compared side by side.',
    readTime: '11 min',
    icon: 'ri-heart-pulse-line',
    accentColor: 'text-rose-700',
    bgColor: 'bg-rose-50',
  },
  {
    slug: 'wealth-building-life-insurance-indianapolis',
    title: 'Building Wealth with Life Insurance',
    excerpt: 'Tax-free growth, infinite banking, and generational wealth strategies using permanent life.',
    readTime: '11 min',
    icon: 'ri-funds-box-line',
    accentColor: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
  },
];

interface CityPage {
  path: string;
  city: string;
  tagline: string;
  description: string;
  icon: string;
  image: string;
  employers: string;
}

const CITY_PAGES: CityPage[] = [
  {
    path: '/life-insurance-fort-wayne',
    city: 'Fort Wayne',
    tagline: 'Indiana\'s Second City',
    description: 'Life insurance for Fort Wayne families — factory workers, Sweetwater freelancers, and Veterans Memorial Healthcare employees.',
    icon: 'ri-building-2-line',
    image: 'https://public.readdy.ai/ai/img_res/3f37c200278327b1ed30707a9bac20a2.jpg',
    employers: 'Parkview Health · Sweetwater Sound · Lincoln Financial',
  },
  {
    path: '/life-insurance-south-bend',
    city: 'South Bend',
    tagline: 'Notre Dame Country',
    description: 'Coverage for South Bend residents — ND faculty, AM General workers, and young families in St. Joseph County.',
    icon: 'ri-graduation-cap-line',
    image: 'https://readdy.ai/api/search-image?query=South%20Bend%20Indiana%20Notre%20Dame%20campus%20neighborhood%20clean%20residential%20suburban%20sunny%20day%20vibrant%20community&width=480&height=300&seq=hub-city-sb&orientation=landscape',
    employers: 'Notre Dame · AM General · Memorial Hospital',
  },
  {
    path: '/life-insurance-evansville',
    city: 'Evansville',
    tagline: 'River City Coverage',
    description: 'Life insurance for Evansville workers — Toyota, ALCOA, and Deaconess employees approaching or near retirement.',
    icon: 'ri-building-3-line',
    image: 'https://readdy.ai/api/search-image?query=Evansville%20Indiana%20river%20city%20industrial%20community%20neighborhood%20clean%20sunny%20day%20urban%20residential&width=480&height=300&seq=hub-city-ev&orientation=landscape',
    employers: 'Toyota · ALCOA · Deaconess Health',
  },
  {
    path: '/life-insurance-carmel',
    city: 'Carmel',
    tagline: 'Hamilton County\'s Wealthiest City',
    description: 'High-income coverage strategies for Carmel professionals — CNO Financial executives, Duke Energy managers, and entrepreneurs.',
    icon: 'ri-award-line',
    image: 'https://readdy.ai/api/search-image?query=Carmel%20Indiana%20upscale%20suburban%20city%20Arts%20District%20roundabouts%20clean%20affluent%20community%20sunny%20day%20beautiful&width=480&height=300&seq=hub-city-carmel&orientation=landscape',
    employers: 'CNO Financial · Duke Energy · Salesforce',
  },
  {
    path: '/life-insurance-fishers',
    city: 'Fishers',
    tagline: 'Indiana\'s Fastest-Growing City',
    description: 'Life insurance for Fishers tech professionals and growing families — Roche, Navient, and Launch Fishers startup founders.',
    icon: 'ri-rocket-2-line',
    image: 'https://readdy.ai/api/search-image?query=Fishers%20Indiana%20fast%20growing%20suburban%20city%20modern%20development%20community%20park%20sunny%20day%20clean%20affluent%20neighborhood&width=480&height=300&seq=hub-city-fishers&orientation=landscape',
    employers: 'Roche Diagnostics · Navient · Genesys',
  },
  {
    path: '/life-insurance-noblesville',
    city: 'Noblesville',
    tagline: 'Hamilton County Homeowners',
    description: 'Mortgage protection and family coverage for Noblesville residents — Riverview Health workers and county government employees.',
    icon: 'ri-home-4-line',
    image: 'https://readdy.ai/api/search-image?query=Noblesville%20Indiana%20charming%20small%20city%20historic%20downtown%20square%20clean%20residential%20community%20sunny%20day%20neighborhood&width=480&height=300&seq=hub-city-noble&orientation=landscape',
    employers: 'Riverview Health · Hamilton County · Ruoff Music',
  },
  {
    path: '/life-insurance-hamilton-county',
    city: 'Hamilton County',
    tagline: 'Indiana\'s Wealthiest County',
    description: 'Comprehensive coverage for all of Hamilton County — including Carmel, Fishers, Noblesville, and Westfield families.',
    icon: 'ri-map-pin-2-line',
    image: 'https://readdy.ai/api/search-image?query=Hamilton%20County%20Indiana%20suburban%20landscape%20aerial%20view%20parks%20neighborhoods%20beautiful%20residential%20community%20clean%20affluent&width=480&height=300&seq=hub-city-hc&orientation=landscape',
    employers: 'All of Hamilton County · 4 Major Cities',
  },
];

interface Article {
  slug: string;
  title: string;
  excerpt: string;
  readTime: string;
  image: string;
  isPillar?: boolean;
}

interface Cluster {
  id: string;
  icon: string;
  label: string;
  color: string;
  bgLight: string;
  borderColor: string;
  pillarLabel: string;
  description: string;
  articles: Article[];
}

const CLUSTERS: Cluster[] = [
  {
    id: 'coverage-planning',
    icon: 'ri-calculator-line',
    label: 'Coverage Planning',
    color: 'text-[#1A3A52]',
    bgLight: 'bg-[#1A3A52]/5',
    borderColor: 'border-[#1A3A52]',
    pillarLabel: 'Start Here',
    description: 'Figure out how much you need, what it will cost, and how to compare carriers before you buy.',
    articles: [
      {
        slug: 'how-much-life-insurance-indiana',
        title: 'How Much Life Insurance Do I Need in Indiana?',
        excerpt: 'The DIME calculator with Indiana-specific mortgage, income, and college cost data. The most important article to read first.',
        readTime: '10 min',
        image: 'https://readdy.ai/api/search-image?query=Indiana%20family%20at%20kitchen%20table%20reviewing%20life%20insurance%20calculator%20worksheet%20warm%20home%20lighting%20natural%20daylight%20clean%20white%20background%20professional%20couple&width=400&height=260&seq=hub-howmuch&orientation=landscape',
        isPillar: true,
      },
      {
        slug: 'life-insurance-quotes-indiana',
        title: 'Life Insurance Quotes Indiana: Get Accurate Rates',
        excerpt: 'What determines your quote, real pricing by age and health class, and how to compare 10+ carriers side-by-side.',
        readTime: '10 min',
        image: 'https://readdy.ai/api/search-image?query=Indiana%20insurance%20agent%20comparing%20life%20insurance%20quotes%20on%20computer%20screen%20bright%20professional%20office%20clean%20minimal%20background%20rate%20comparison&width=400&height=260&seq=hub-quotes&orientation=landscape',
      },
      {
        slug: 'cheapest-life-insurance-indiana',
        title: 'Cheapest Life Insurance in Indiana: Save in 2025',
        excerpt: '6 proven strategies that lower your rate — with real carrier comparisons showing exactly how much Hoosiers save.',
        readTime: '9 min',
        image: 'https://readdy.ai/api/search-image?query=smiling%20Indiana%20couple%20reviewing%20affordable%20life%20insurance%20rates%20on%20laptop%20bright%20modern%20home%20desk%20savings%20clean%20white%20minimal%20background&width=400&height=260&seq=hub-cheapest&orientation=landscape',
      },
      {
        slug: 'best-life-insurance-indianapolis',
        title: 'Best Life Insurance Companies Indianapolis 2025',
        excerpt: 'Top-rated carriers ranked by price, financial strength, and customer service for Indianapolis families.',
        readTime: '13 min',
        image: 'https://readdy.ai/api/search-image?query=insurance%20agent%20shaking%20hands%20with%20happy%20client%20bright%20modern%20office%20Indianapolis%20Indiana%20professional%20clean%20background%20trust&width=400&height=260&seq=hub-best&orientation=landscape',
      },
      {
        slug: 'life-insurance-rates-indianapolis',
        title: 'Life Insurance Rates Indianapolis: 2025 Prices',
        excerpt: 'Exact prices by age, health class, and coverage amount — and how to secure the lowest rate for your profile.',
        readTime: '11 min',
        image: 'https://readdy.ai/api/search-image?query=calculator%20and%20insurance%20rate%20comparison%20chart%20clean%20white%20desk%20minimal%20professional%20background%20Indianapolis%20life%20insurance%20pricing&width=400&height=260&seq=hub-rates&orientation=landscape',
      },
    ],
  },
  {
    id: 'family-coverage',
    icon: 'ri-group-2-line',
    label: 'Family Coverage',
    color: 'text-emerald-700',
    bgLight: 'bg-emerald-50',
    borderColor: 'border-emerald-500',
    pillarLabel: 'Family Pillar',
    description: 'Coverage strategies for every stage of family life — from newlyweds to parents of teenagers.',
    articles: [
      {
        slug: 'life-insurance-for-families-indianapolis',
        title: 'Best Life Insurance for Indianapolis Families',
        excerpt: 'Coverage amounts, best policy types, and real 2025 pricing for every family stage in Central Indiana.',
        readTime: '11 min',
        image: 'https://readdy.ai/api/search-image?query=happy%20Indianapolis%20family%20of%20four%20at%20home%20parents%20two%20young%20children%20smiling%20bright%20living%20room%20clean%20modern%20warm%20natural%20light&width=400&height=260&seq=hub-families&orientation=landscape',
        isPillar: true,
      },
      {
        slug: 'life-insurance-new-parents-indianapolis',
        title: 'Life Insurance for New Parents Indianapolis',
        excerpt: 'Why new parents need life insurance immediately, how much coverage you need, and how to apply fast.',
        readTime: '8 min',
        image: 'https://readdy.ai/api/search-image?query=young%20parents%20holding%20newborn%20baby%20in%20bright%20cozy%20nursery%20Indianapolis%20Indiana%20warm%20soft%20lighting%20clean%20minimal%20background%20proud%20happy&width=400&height=260&seq=hub-newparents&orientation=landscape',
      },
      {
        slug: 'life-insurance-stay-at-home-parents-indiana',
        title: 'Life Insurance for Stay-at-Home Parents Indiana',
        excerpt: 'SAHPs provide $92K+ in annual services. Why you need $500K+ in coverage and how to get it for under $30/month.',
        readTime: '12 min',
        image: 'https://readdy.ai/api/search-image?query=stay%20at%20home%20parent%20Indiana%20mom%20playing%20with%20two%20young%20children%20in%20bright%20sunny%20living%20room%20warm%20natural%20light%20cozy%20family%20home%20clean%20minimal%20background%20loving&width=400&height=260&seq=hub-sahp&orientation=landscape',
      },
      {
        slug: 'mortgage-protection-indianapolis',
        title: 'Mortgage Protection Insurance Indianapolis',
        excerpt: 'How to keep your family\'s home safe if something happens to you — costs, coverage, and how it works.',
        readTime: '9 min',
        image: 'https://readdy.ai/api/search-image?query=family%20standing%20in%20front%20of%20beautiful%20suburban%20home%20Indianapolis%20Indiana%20sunny%20day%20green%20lawn%20clean%20white%20background%20happy%20homeowners&width=400&height=260&seq=hub-mortgage&orientation=landscape',
      },
    ],
  },
  {
    id: 'term-life',
    icon: 'ri-time-line',
    label: 'Term Life Insurance',
    color: 'text-[#D97642]',
    bgLight: 'bg-[#D97642]/5',
    borderColor: 'border-[#D97642]',
    pillarLabel: 'Term Pillar',
    description: 'Pure protection for the years that matter most — term life explained for Indiana families.',
    articles: [
      {
        slug: 'term-life-insurance-indianapolis',
        title: 'Term Life Insurance Indianapolis: Complete 2025 Guide',
        excerpt: 'Everything Indianapolis families need to know about term life — costs, carriers, and how to apply.',
        readTime: '12 min',
        image: 'https://readdy.ai/api/search-image?query=professional%20family%20sitting%20at%20table%20reviewing%20life%20insurance%20documents%20in%20bright%20modern%20Indianapolis%20home%20warm%20natural%20light%20clean%20white%20background&width=400&height=260&seq=hub-term&orientation=landscape',
        isPillar: true,
      },
      {
        slug: 'term-vs-whole-life-indianapolis',
        title: 'Term vs. Whole Life Insurance Indianapolis',
        excerpt: 'Side-by-side cost and benefit comparison — which policy type actually fits your family\'s situation.',
        readTime: '12 min',
        image: 'https://readdy.ai/api/search-image?query=two%20insurance%20policy%20documents%20side%20by%20side%20on%20clean%20white%20desk%20comparison%20concept%20minimal%20professional%20background%20financial%20planning&width=400&height=260&seq=hub-termvswhole&orientation=landscape',
      },
      {
        slug: 'no-medical-exam-life-insurance-indiana',
        title: 'No Medical Exam Life Insurance Indiana',
        excerpt: 'Same-day approval, no blood draws. Simplified issue vs. guaranteed issue compared side by side.',
        readTime: '11 min',
        image: 'https://readdy.ai/api/search-image?query=relaxed%20Indiana%20man%20completing%20life%20insurance%20application%20on%20laptop%20at%20bright%20home%20office%20clean%20white%20minimal%20background%20no%20stress%20easy%20approval&width=400&height=260&seq=hub-noexam&orientation=landscape',
      },
    ],
  },
  {
    id: 'permanent-life',
    icon: 'ri-shield-star-line',
    label: 'Permanent Life Insurance',
    color: 'text-teal-700',
    bgLight: 'bg-teal-50',
    borderColor: 'border-teal-500',
    pillarLabel: 'Permanent Pillar',
    description: 'Coverage that lasts a lifetime — and builds real cash value you can access while you\'re alive.',
    articles: [
      {
        slug: 'whole-life-insurance-indianapolis',
        title: 'Whole Life Insurance Indianapolis: Permanent Coverage',
        excerpt: 'How whole life provides lifetime protection and guaranteed cash value growth for Indiana families.',
        readTime: '10 min',
        image: 'https://readdy.ai/api/search-image?query=mature%20couple%20smiling%20reviewing%20financial%20documents%20at%20home%20desk%20warm%20lighting%20clean%20minimal%20background%20Indianapolis%20Indiana%20whole%20life%20planning&width=400&height=260&seq=hub-wholelife&orientation=landscape',
        isPillar: true,
      },
      {
        slug: 'indexed-universal-life-indianapolis',
        title: 'Indexed Universal Life Insurance Indianapolis',
        excerpt: 'Market-linked growth with downside protection, tax-free loans, and retirement income potential.',
        readTime: '11 min',
        image: 'https://readdy.ai/api/search-image?query=stock%20market%20growth%20chart%20on%20screen%20with%20insurance%20documents%20on%20desk%20clean%20minimal%20office%20background%20warm%20tones%20IUL%20policy%20planning&width=400&height=260&seq=hub-iul&orientation=landscape',
      },
      {
        slug: 'universal-life-insurance-indianapolis',
        title: 'Universal Life Insurance Indianapolis',
        excerpt: 'Flexible premiums and adjustable death benefits — how UL works and who benefits most.',
        readTime: '10 min',
        image: 'https://readdy.ai/api/search-image?query=financial%20advisor%20explaining%20insurance%20policy%20flexible%20options%20to%20couple%20in%20modern%20office%20clean%20white%20background%20professional%20Indianapolis&width=400&height=260&seq=hub-universallife&orientation=landscape',
      },
      {
        slug: 'wealth-building-life-insurance-indianapolis',
        title: 'Building Wealth with Life Insurance Indianapolis',
        excerpt: 'Tax-free growth, infinite banking, and generational wealth strategies using permanent life.',
        readTime: '11 min',
        image: 'https://readdy.ai/api/search-image?query=wealth%20building%20concept%20with%20life%20insurance%20policy%20documents%20investment%20growth%20charts%20financial%20planning%20executive%20desk%20professional%20Indianapolis&width=400&height=260&seq=hub-wealthbuild&orientation=landscape',
      },
    ],
  },
  {
    id: 'seniors-final-expense',
    icon: 'ri-user-heart-line',
    label: 'Seniors & Final Expense',
    color: 'text-amber-700',
    bgLight: 'bg-amber-50',
    borderColor: 'border-amber-500',
    pillarLabel: 'Seniors Pillar',
    description: 'Coverage options designed specifically for Indiana seniors — no-exam options and final expense plans.',
    articles: [
      {
        slug: 'life-insurance-indiana-seniors',
        title: 'Life Insurance for Seniors in Indiana (2025)',
        excerpt: 'Best options for ages 60–85 — final expense, guaranteed issue, and term life compared with real rates.',
        readTime: '11 min',
        image: 'https://readdy.ai/api/search-image?query=active%20senior%20couple%20in%20their%2060s%20smiling%20outdoors%20Indiana%20park%20autumn%20leaves%20golden%20hour%20warm%20light%20healthy%20retired%20lifestyle%20cheerful%20clean%20blurred&width=400&height=260&seq=hub-seniors&orientation=landscape',
        isPillar: true,
      },
      {
        slug: 'final-expense-insurance-indianapolis',
        title: 'Final Expense Insurance Indianapolis',
        excerpt: 'Costs, coverage amounts, top carriers, and how Indianapolis seniors qualify for final expense coverage.',
        readTime: '8 min',
        image: 'https://readdy.ai/api/search-image?query=senior%20couple%20sitting%20together%20reviewing%20insurance%20paperwork%20at%20kitchen%20table%20warm%20cozy%20home%20Indianapolis%20Indiana%20soft%20natural%20light%20compassionate&width=400&height=260&seq=hub-finalexpense&orientation=landscape',
      },
      {
        slug: 'burial-insurance-indiana',
        title: 'Burial Insurance Indiana: Costs & Best Plans',
        excerpt: 'Indiana funerals average $9,500–$11,650. Real rates by age for $10K–$25K policies and guaranteed approval options.',
        readTime: '10 min',
        image: 'https://readdy.ai/api/search-image?query=dignified%20senior%20Indiana%20couple%20reviewing%20burial%20insurance%20documents%20peaceful%20warm%20living%20room%20natural%20light%20compassionate%20planning%20family%20home&width=400&height=260&seq=hub-burial&orientation=landscape',
      },
      {
        slug: 'life-insurance-seniors-over-60-indianapolis',
        title: 'Life Insurance for Seniors Over 60 Indianapolis',
        excerpt: 'Best coverage options, real rates, and guaranteed approval policies for Indianapolis seniors aged 60–85.',
        readTime: '9 min',
        image: 'https://readdy.ai/api/search-image?query=happy%20healthy%20senior%20couple%20over%2060%20Indianapolis%20Indiana%20reviewing%20insurance%20documents%20at%20home%20natural%20warm%20light%20retirement%20lifestyle%20smiling&width=400&height=260&seq=hub-seniors60&orientation=landscape',
      },
    ],
  },
  {
    id: 'special-circumstances',
    icon: 'ri-stethoscope-line',
    label: 'Special Circumstances',
    color: 'text-rose-700',
    bgLight: 'bg-rose-50',
    borderColor: 'border-rose-400',
    pillarLabel: 'Health & Lifestyle',
    description: 'Getting approved when health conditions, lifestyle, or occupation complicates your application.',
    articles: [
      {
        slug: 'life-insurance-smokers-indiana',
        title: 'Life Insurance for Smokers Indiana: 2025 Rates',
        excerpt: 'How much more smokers pay in Indiana, which carriers are most lenient, and when you qualify for non-smoker rates.',
        readTime: '10 min',
        image: 'https://readdy.ai/api/search-image?query=Indiana%20adult%20researching%20life%20insurance%20options%20on%20laptop%20at%20home%20bright%20clean%20modern%20desk%20determined%20person%20seeking%20coverage%20warm%20light%20professional%20setting&width=400&height=260&seq=hub-smokers&orientation=landscape',
      },
      {
        slug: 'life-insurance-diabetes-indianapolis',
        title: 'Life Insurance with Diabetes Indianapolis',
        excerpt: 'Get approved with Type 1 or Type 2 diabetes — A1C guidelines by carrier and what rates to expect.',
        readTime: '10 min',
        image: 'https://readdy.ai/api/search-image?query=diabetic%20person%20reviewing%20life%20insurance%20application%20forms%20at%20home%20warm%20natural%20light%20hopeful%20confident%20clean%20minimal%20background&width=400&height=260&seq=hub-diabetes&orientation=landscape',
      },
      {
        slug: 'life-insurance-high-blood-pressure',
        title: 'Life Insurance with High Blood Pressure Indiana',
        excerpt: 'BP guidelines carrier by carrier, approval tips for hypertension, and how blood pressure affects your rate.',
        readTime: '9 min',
        image: 'https://readdy.ai/api/search-image?query=Indiana%20person%20with%20blood%20pressure%20monitor%20reviewing%20life%20insurance%20documents%20at%20home%20confident%20determined%20clean%20minimal%20background%20natural%20light&width=400&height=260&seq=hub-highbp&orientation=landscape',
      },
      {
        slug: 'life-insurance-private-pilots',
        title: 'Life Insurance for Private Pilots Indiana',
        excerpt: 'How aviation risk affects your application, which carriers are pilot-friendly, and how to get approved.',
        readTime: '8 min',
        image: 'https://readdy.ai/api/search-image?query=private%20pilot%20standing%20next%20to%20small%20aircraft%20at%20Indianapolis%20airport%20sunny%20day%20clean%20background%20professional%20confident%20aviation%20lifestyle&width=400&height=260&seq=hub-pilots&orientation=landscape',
      },
    ],
  },
  {
    id: 'business-self-employed',
    icon: 'ri-briefcase-3-line',
    label: 'Business & Self-Employed',
    color: 'text-[#1A3A52]',
    bgLight: 'bg-slate-50',
    borderColor: 'border-slate-400',
    pillarLabel: 'Business Coverage',
    description: 'Life insurance strategies for Indiana\'s 340,000+ self-employed workers and small business owners.',
    articles: [
      {
        slug: 'life-insurance-self-employed-indiana',
        title: 'Life Insurance for Self-Employed Indiana',
        excerpt: 'Why 1099 workers need more coverage than W-2 employees — the complete coverage, cost, and tax guide.',
        readTime: '12 min',
        image: 'https://readdy.ai/api/search-image?query=self%20employed%20Indiana%20small%20business%20owner%20entrepreneur%20reviewing%20life%20insurance%20documents%20at%20modern%20home%20office%20desk%20laptop%20coffee%20clean%20bright%20minimal%20background%20professional&width=400&height=260&seq=hub-selfemployed&orientation=landscape',
        isPillar: true,
      },
      {
        slug: 'business-key-person-insurance-indianapolis',
        title: 'Key Person Life Insurance Indianapolis',
        excerpt: 'How to protect your Indianapolis business from the financial impact of losing a key employee or owner.',
        readTime: '10 min',
        image: 'https://readdy.ai/api/search-image?query=business%20owner%20in%20modern%20Indianapolis%20office%20reviewing%20key%20person%20insurance%20documents%20professional%20clean%20background%20warm%20lighting%20executive%20management&width=400&height=260&seq=hub-keyperson&orientation=landscape',
      },
    ],
  },
  {
    id: 'wealth-retirement',
    icon: 'ri-funds-box-line',
    label: 'Wealth Building & Retirement',
    color: 'text-yellow-700',
    bgLight: 'bg-yellow-50',
    borderColor: 'border-yellow-500',
    pillarLabel: 'Wealth & Retirement',
    description: 'Using insurance and annuities to build tax-advantaged wealth and secure guaranteed lifetime income.',
    articles: [
      {
        slug: 'annuities-retirement-indianapolis',
        title: 'Annuities & Retirement Planning Indianapolis',
        excerpt: 'Guaranteed income you can\'t outlive — how annuities work, fixed vs. indexed vs. variable compared.',
        readTime: '10 min',
        image: 'https://readdy.ai/api/search-image?query=retired%20couple%20enjoying%20outdoor%20patio%20with%20financial%20planning%20documents%20on%20table%20sunny%20day%20Indianapolis%20Indiana%20clean%20background%20retirement%20income&width=400&height=260&seq=hub-annuities&orientation=landscape',
        isPillar: true,
      },
      {
        slug: 'wealth-building-life-insurance-indianapolis',
        title: 'Wealth Building with Life Insurance Indianapolis',
        excerpt: 'Tax-free growth strategies using permanent life insurance — infinite banking, college funding, and retirement.',
        readTime: '11 min',
        image: 'https://readdy.ai/api/search-image?query=wealth%20building%20concept%20with%20life%20insurance%20policy%20documents%20investment%20growth%20charts%20financial%20planning%20materials%20executive%20desk%20professional&width=400&height=260&seq=hub-wealth&orientation=landscape',
      },
    ],
  },
];

export default function LearningHubPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Life Insurance Learning Hub Indiana | 27 Guides + 7 City Pages';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Indiana\'s complete life insurance resource hub — 27 expert guides organized by topic plus dedicated coverage pages for Fort Wayne, South Bend, Evansville, Carmel, Fishers, Noblesville & Hamilton County.');
    setMeta('keywords', 'life insurance Indiana, Indiana life insurance learning hub, Indianapolis life insurance, Indiana life insurance by city, Indiana insurance guides, Fort Wayne life insurance, Carmel life insurance');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Life Insurance Learning Hub — Indiana',
      description: '27 expert guides + 7 Indiana city coverage pages organized by topical cluster for Hoosier families.',
      url: CANONICAL,
      isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Learning Hub', item: CANONICAL },
        ],
      },
      mainEntity: {
        '@type': 'ItemList',
        name: 'Indiana Life Insurance Guides & City Pages',
        numberOfItems: 34,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'How Much Life Insurance Do I Need in Indiana?', url: `${SITE_URL}/blog/how-much-life-insurance-indiana` },
          { '@type': 'ListItem', position: 2, name: 'Term Life Insurance Indianapolis', url: `${SITE_URL}/blog/term-life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Mortgage Protection Insurance Indianapolis', url: `${SITE_URL}/blog/mortgage-protection-indianapolis` },
          { '@type': 'ListItem', position: 4, name: 'No Medical Exam Life Insurance Indiana', url: `${SITE_URL}/blog/no-medical-exam-life-insurance-indiana` },
          { '@type': 'ListItem', position: 5, name: 'Building Wealth with Life Insurance Indianapolis', url: `${SITE_URL}/blog/wealth-building-life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 6, name: 'Life Insurance Fort Wayne Indiana', url: `${SITE_URL}/life-insurance-fort-wayne` },
          { '@type': 'ListItem', position: 7, name: 'Life Insurance South Bend Indiana', url: `${SITE_URL}/life-insurance-south-bend` },
          { '@type': 'ListItem', position: 8, name: 'Life Insurance Evansville Indiana', url: `${SITE_URL}/life-insurance-evansville` },
          { '@type': 'ListItem', position: 9, name: 'Life Insurance Carmel Indiana', url: `${SITE_URL}/life-insurance-carmel` },
          { '@type': 'ListItem', position: 10, name: 'Life Insurance Fishers Indiana', url: `${SITE_URL}/life-insurance-fishers` },
          { '@type': 'ListItem', position: 11, name: 'Life Insurance Noblesville Indiana', url: `${SITE_URL}/life-insurance-noblesville` },
          { '@type': 'ListItem', position: 12, name: 'Life Insurance Hamilton County Indiana', url: `${SITE_URL}/life-insurance-hamilton-county` },
        ],
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-learning-hub';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.getElementById('schema-learning-hub')?.remove();
    };
  }, []);

  const totalArticles = CLUSTERS.reduce((acc, c) => acc + c.articles.length, 0);

  return (
    <>
      <title>Life Insurance Learning Hub Indiana | 27 Guides + 7 City Pages | Hoosier Life Insurance</title>
      <div className="min-h-screen bg-[#FAFAF8]">
        <Header />

        {/* Hero */}
        <section className="bg-[#1A3A52] pt-28 pb-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#D97642] blur-3xl translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-1/2 w-80 h-80 rounded-full bg-white blur-3xl -translate-x-1/2 translate-y-1/2"></div>
          </div>
          <div className="max-w-6xl mx-auto px-6 relative">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm mb-6">
              <Link to="/" className="text-white/60 hover:text-[#D97642] transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-white/30"></i>
              <span className="text-[#D97642] font-medium">Learning Hub</span>
            </nav>
            <div className="max-w-3xl">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center gap-2 bg-[#D97642]/20 text-[#D97642] text-xs font-semibold px-3 py-1.5 rounded-full">
                  <i className="ri-book-open-line"></i>
                  {totalArticles} Expert Guides
                </span>
                <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                  <i className="ri-map-pin-2-line"></i>
                  7 Indiana City Pages
                </span>
                <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                  <i className="ri-star-line text-[#D97642]"></i>
                  5 Pillar Guides
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                Life Insurance Learning Hub for Indiana Families
              </h1>
              <p className="text-white/70 text-lg leading-relaxed max-w-2xl">
                Indiana's most complete life insurance resource — {totalArticles} expert guides organized by topic, plus dedicated coverage pages for every major city. Find exactly what you need and follow the links to build a complete picture fast.
              </p>
            </div>

            {/* Cluster Quick-Nav */}
            <div className="mt-8 flex flex-wrap gap-2">
              <a
                href="#pillar-guides"
                className="inline-flex items-center gap-1.5 bg-[#D97642]/30 hover:bg-[#D97642]/50 text-white text-xs font-bold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap border border-[#D97642]/40"
              >
                <i className="ri-star-line text-[#D97642]"></i>
                Start Here — 5 Pillars
              </a>
              {CLUSTERS.map(cluster => (
                <a
                  key={cluster.id}
                  href={`#${cluster.id}`}
                  className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cluster.icon} text-[#D97642]`}></i>
                  {cluster.label}
                </a>
              ))}
              <a
                href="#coverage-by-city"
                className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-pin-2-line text-[#D97642]"></i>
                Coverage By City
              </a>
            </div>
          </div>
        </section>

        {/* How to Use This Hub */}
        <section className="bg-white border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-6 py-8">
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: 'ri-compass-3-line', color: 'text-[#D97642]', title: 'New to life insurance?', body: 'Start with "Coverage Planning" — the How Much guide is designed to be read first by every Indiana family.' },
                { icon: 'ri-search-2-line', color: 'text-emerald-600', title: 'Have a specific situation?', body: 'Jump directly to your cluster — Family Coverage, Special Circumstances, Seniors, or Business — and read the pillar article first.' },
                { icon: 'ri-links-line', color: 'text-[#1A3A52]', title: 'Already researching?', body: 'Each article links to 3 related guides. Follow the internal links from article to article to build a complete picture fast.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-gray-50 rounded-xl border border-gray-100">
                    <i className={`${item.icon} ${item.color} text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{item.title}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <main className="max-w-6xl mx-auto px-6 py-14 space-y-20">

          {/* ── 5 Pillar Guides — Start Here ── */}
          <section id="pillar-guides" className="scroll-mt-24">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/10 rounded-xl">
                <i className="ri-star-line text-[#D97642] text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#D97642]">Foundation Reading</span>
                <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] leading-snug">5 Pillar Guides — Start Here</h2>
              </div>
            </div>
            <p className="text-gray-500 text-sm max-w-2xl mb-8 pl-0">
              These are the five guides every Indiana family should read first. Each covers a core concept that shapes every insurance decision — from how much you need to how to pay for it.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {PILLAR_GUIDES.map((guide, idx) => (
                <Link
                  key={guide.slug}
                  to={`/blog/${guide.slug}`}
                  className="group relative bg-white rounded-2xl border-2 border-[#D97642]/25 hover:border-[#D97642] transition-all duration-300 flex flex-col p-5 cursor-pointer overflow-hidden"
                >
                  <div className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center bg-[#D97642] rounded-full text-white text-[10px] font-black">
                    {idx + 1}
                  </div>
                  <div className={`w-10 h-10 flex items-center justify-center ${guide.bgColor} rounded-xl mb-3 flex-shrink-0`}>
                    <i className={`${guide.icon} ${guide.accentColor} text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-[13px] text-[#1A3A52] leading-snug mb-2 group-hover:text-[#D97642] transition-colors pr-6 line-clamp-2">
                    {guide.title}
                  </h3>
                  <p className="text-[11px] text-gray-500 leading-relaxed flex-1 line-clamp-3 mb-3">
                    {guide.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-[11px] text-gray-400 flex items-center gap-1">
                      <i className="ri-time-line"></i> {guide.readTime}
                    </span>
                    <span className="text-[11px] font-bold text-[#D97642] flex items-center gap-0.5 group-hover:gap-1.5 transition-all">
                      Read <i className="ri-arrow-right-line text-[12px]"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {CLUSTERS.map(cluster => (
            <section key={cluster.id} id={cluster.id} className="scroll-mt-24">

              {/* Cluster Header */}
              <div className={`border-l-4 ${cluster.borderColor} pl-5 mb-8`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 flex items-center justify-center ${cluster.bgLight} rounded-xl`}>
                    <i className={`${cluster.icon} ${cluster.color} text-xl`}></i>
                  </div>
                  <div>
                    <span className={`text-xs font-bold uppercase tracking-widest ${cluster.color}`}>{cluster.label}</span>
                    <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] leading-snug">{cluster.label}</h2>
                  </div>
                </div>
                <p className="text-gray-500 text-sm max-w-2xl">{cluster.description}</p>
              </div>

              {/* Article Grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {cluster.articles.map(article => (
                  <Link
                    key={article.slug}
                    to={`/blog/${article.slug}`}
                    className={`group relative bg-white rounded-2xl overflow-hidden border hover:border-[#D97642]/40 transition-all duration-300 flex flex-col cursor-pointer ${article.isPillar ? `border-2 ${cluster.borderColor}` : 'border-gray-100'}`}
                  >
                    {article.isPillar && (
                      <div className={`absolute top-3 right-3 z-10 text-[10px] font-black px-2 py-1 rounded-full ${cluster.bgLight} ${cluster.color} border ${cluster.borderColor}`}>
                        {cluster.pillarLabel}
                      </div>
                    )}
                    <div className="w-full h-40 overflow-hidden flex-shrink-0">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={article.title}
                        loading="lazy"
                        decoding="async"
                        width={400}
                        height={260}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h3 className={`font-bold text-[14px] leading-snug mb-2 group-hover:text-[#D97642] transition-colors line-clamp-2 ${article.isPillar ? 'text-[#1A3A52]' : 'text-gray-900'}`}>
                        {article.title}
                      </h3>
                      <p className="text-[12px] text-gray-500 leading-relaxed flex-1 line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <span className="text-[11px] text-gray-400 flex items-center gap-1">
                          <i className="ri-time-line"></i> {article.readTime}
                        </span>
                        <span className="text-[12px] font-semibold text-[#D97642] flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap flex-shrink-0">
                          Read <i className="ri-arrow-right-line text-[13px]"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}

          {/* ── Coverage By City ── */}
          <section id="coverage-by-city" className="scroll-mt-24">
            <div className="border-l-4 border-[#D97642] pl-5 mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/8 rounded-xl">
                  <i className="ri-map-pin-2-line text-[#D97642] text-xl"></i>
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#D97642]">Local Coverage</span>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] leading-snug">Coverage By City</h2>
                </div>
              </div>
              <p className="text-gray-500 text-sm max-w-2xl">
                Life insurance rates, top carriers, and employer-specific strategies vary city by city across Indiana. Each page below is built specifically for that community — with local context, employer-matched advice, and an agent who actually lives and works there.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {CITY_PAGES.map(city => (
                <Link
                  key={city.path}
                  to={city.path}
                  className="group bg-white rounded-2xl border border-gray-100 hover:border-[#D97642]/40 transition-all duration-300 flex flex-col overflow-hidden cursor-pointer"
                >
                  <div className="w-full h-40 overflow-hidden flex-shrink-0 relative">
                    <img
                      src={city.image}
                      alt={`Life insurance ${city.city} Indiana`}
                      title={`Life Insurance ${city.city} Indiana`}
                      loading="lazy"
                      decoding="async"
                      width={480}
                      height={300}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A3A52]/60 to-transparent"></div>
                    <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                      <div>
                        <p className="text-[10px] text-white/70 font-semibold uppercase tracking-widest">{city.tagline}</p>
                        <h3 className="text-white font-bold text-base leading-tight">{city.city}</h3>
                      </div>
                      <div className="w-8 h-8 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                        <i className={`${city.icon} text-white text-sm`}></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <p className="text-[12px] text-gray-600 leading-relaxed flex-1 mb-3 line-clamp-2">
                      {city.description}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-[10px] text-gray-400 flex items-center gap-1 truncate mr-2">
                        <i className="ri-briefcase-3-line flex-shrink-0"></i>
                        <span className="truncate">{city.employers}</span>
                      </span>
                      <span className="text-[11px] font-bold text-[#D97642] flex items-center gap-0.5 group-hover:gap-1.5 transition-all whitespace-nowrap flex-shrink-0">
                        View Page <i className="ri-arrow-right-line text-[12px]"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Indiana state-wide link */}
            <div className="mt-6 bg-[#1A3A52]/4 rounded-2xl border border-[#1A3A52]/10 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#1A3A52] rounded-xl flex-shrink-0">
                  <i className="ri-map-2-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A3A52] text-sm">Looking for statewide Indiana coverage?</h3>
                  <p className="text-gray-500 text-[12px]">Our Indiana state page compares coverage across all 92 counties with carrier rates, health class data, and how to get quotes statewide.</p>
                </div>
              </div>
              <Link
                to="/life-insurance-indiana"
                className="px-5 py-2.5 bg-[#1A3A52] text-white rounded-lg font-bold text-sm hover:bg-[#0F2A3F] transition-colors whitespace-nowrap flex-shrink-0"
              >
                Indiana State Guide
              </Link>
            </div>
          </section>

          {/* Bottom CTA */}
          <section className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-10 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-[#D97642]/10 blur-3xl translate-x-1/2 -translate-y-1/2"></div>
            <div className="relative">
              <div className="w-14 h-14 flex items-center justify-center bg-[#D97642]/20 rounded-full mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-[#D97642]"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">Ready to Move from Research to Coverage?</h2>
              <p className="text-white/70 mb-7 max-w-xl mx-auto">
                A licensed Indianapolis agent can take everything you've learned here and turn it into a specific recommendation for your family — in one free, no-pressure call.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/instant-quote"
                  className="px-7 py-3.5 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap"
                >
                  Get Instant Quote
                </Link>
                <Link
                  to="/book-appointment"
                  className="px-7 py-3.5 bg-white/10 text-white rounded-lg font-bold hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap"
                >
                  Book Free Consultation
                </Link>
                <Link
                  to="/blog"
                  className="px-7 py-3.5 bg-white/10 text-white rounded-lg font-bold hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap"
                >
                  Browse All {totalArticles} Articles
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
